import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  adminAnalyticsKeys,
  useAnalyticsRefreshStatus,
  useTriggerAnalyticsRefresh,
} from "@/hooks/useAdminAnalytics";
import {
  AnalyticsRefreshStatus,
  PlatformAnalyticsRefreshRunDto,
  TriggerAnalyticsRefreshRequest,
} from "@/types/admin-analytics";

interface AnalyticsRefreshFlow {
  run: PlatformAnalyticsRefreshRunDto | null;
  isRunning: boolean;
  isStatusLoading: boolean;
  isTriggering: boolean;
  triggerRefresh: (body: TriggerAnalyticsRefreshRequest) => void;
}

/**
 * Drives the refresh lifecycle: trigger → poll latest run → refetch summaries
 * when the run completes (the rollup is the only thing that changes summary data).
 */
export const useAnalyticsRefreshFlow = (): AnalyticsRefreshFlow => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const statusQuery = useAnalyticsRefreshStatus();
  const triggerMutation = useTriggerAnalyticsRefresh();

  const run = statusQuery.data ?? null;
  const status = run?.status;
  const error = run?.error;
  const prevStatusRef = useRef<AnalyticsRefreshStatus | undefined>(undefined);

  useEffect(() => {
    const previous = prevStatusRef.current;
    prevStatusRef.current = status;

    if (previous !== "RUNNING") {
      return;
    }

    if (status === "COMPLETED") {
      queryClient.invalidateQueries({
        queryKey: adminAnalyticsKeys.summaries(),
      });
      toast.success(t("analytics.platform.refreshCompleted"));
    }

    if (status === "FAILED") {
      toast.error(error ?? t("analytics.platform.status.failed"));
    }
  }, [status, error, queryClient, t]);

  return {
    run,
    isRunning: status === "RUNNING",
    isStatusLoading: statusQuery.isLoading,
    isTriggering: triggerMutation.isPending,
    triggerRefresh: (body) => triggerMutation.mutate(body),
  };
};
